import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, CalendarDays, Clock, MapPin, Banknote } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Brand } from "@/components/AppShell";
import { SlotPicker } from "@/components/SlotPicker";

const Result = ({ status }) =>
  status === "confermato" ? (
    <div className="flex items-center gap-3 rounded-2xl bg-emerald-50 p-4 text-emerald-800" data-testid="appt-response-confirmed">
      <CheckCircle2 className="h-6 w-6" /><div><div className="font-bold">Presenza confermata</div><div className="text-sm">Grazie! Ti aspettiamo.</div></div>
    </div>
  ) : (
    <div className="flex items-center gap-3 rounded-2xl bg-rose-50 p-4 text-rose-800" data-testid="appt-response-cancelled">
      <XCircle className="h-6 w-6" /><div><div className="font-bold">Appuntamento disdetto</div><div className="text-sm">La dottoressa è stata avvisata.</div></div>
    </div>
  );

const eur = (v) => (v ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

const PaymentBox = ({ payment, reported, busy, onReport }) => (
  <div className="space-y-2 rounded-2xl border border-[#e3d3bf] p-4 text-sm" data-testid="appt-payment-box">
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#b8956f]"><Banknote className="h-4 w-4" /> Pagamento con bonifico</div>
    <div><span className="text-slate-500">Importo:</span> <strong data-testid="appt-payment-amount">{eur(payment.amount)}</strong></div>
    <div><span className="text-slate-500">Intestatario:</span> <strong>{payment.holder}</strong></div>
    <div><span className="text-slate-500">IBAN:</span> <strong className="break-all" data-testid="appt-payment-iban">{payment.iban}</strong></div>
    <div><span className="text-slate-500">Causale:</span> <strong>{payment.causale}</strong></div>
    {reported ? (
      <div className="flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-emerald-800" data-testid="appt-payment-reported"><CheckCircle2 className="h-5 w-5" /> Grazie! La dottoressa è stata avvisata del pagamento.</div>
    ) : (
      <button className="btn-primary w-full" disabled={busy} onClick={onReport} data-testid="appt-payment-report-btn">Confermo di aver effettuato il pagamento</button>
    )}
  </div>
);

export default function AppointmentResponse() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const action = params.get("azione");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const autoDone = useRef(false);

  const respond = async (a) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/appointment-response/${token}`, { action: a });
      setInfo(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    api.get(`/public/appointment-response/${token}`).then((r) => {
      setInfo(r.data);
      if (action === "conferma" && !autoDone.current && r.data.status === "in_attesa" && !r.data.expired) {
        autoDone.current = true;
        respond("conferma");
      }
      if (action === "pagato" && !autoDone.current && r.data.payment && !r.data.payment_reported) {
        autoDone.current = true;
        respond("pagato");
      }
    }).catch((err) => setError(errMsg(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const canAct = info && !info.expired && info.status !== "disdetto";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8fafc] px-6 py-10">
      <div className="card fade-up w-full max-w-md space-y-6" data-testid="appt-response-page">
        <Brand />
        {error && <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="appt-response-error">{error}</div>}
        {info && (
          <>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight">{info.first_name ? `Ciao ${info.first_name},` : "Il tuo appuntamento"}</h1>
              <p className="mt-1 text-sm text-slate-500">ecco i dettagli della tua visita con la Dott.ssa Simona Marretta.</p>
            </div>
            <div className="space-y-2 rounded-2xl bg-[#edf2ef] p-4 text-sm" data-testid="appt-response-details">
              <div className="flex items-center gap-2 font-semibold capitalize"><CalendarDays className="h-4 w-4 text-[#3d655b]" />{fmtDate(info.date, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#3d655b]" />ore {info.time}</div>
              {info.location && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#3d655b]" />{info.location}</div>}
              {info.reason && <div className="text-slate-600">{info.reason}</div>}
            </div>
            {info.status !== "in_attesa" && <Result status={info.status} />}
            {info.payment && info.status !== "disdetto" && <PaymentBox payment={info.payment} reported={info.payment_reported} busy={busy} onReport={() => respond("pagato")} />}
            {info.status === "disdetto" && info.rescheduled_to && (
              <div className="rounded-2xl bg-[#edf2ef] p-4 text-sm text-[#2f4f47]" data-testid="appt-response-rebooked">
                <div className="font-bold">Nuovo appuntamento fissato</div>
                <div>{info.rescheduled_to}. Riceverai una email di conferma.</div>
              </div>
            )}
            {info.status === "disdetto" && !info.rescheduled_to && !info.expired && (
              <SlotPicker
                loadSlots={() => api.get(`/public/appointment-response/${token}/slots`).then((r) => r.data)}
                book={(date, time) => api.post(`/public/appointment-response/${token}/reschedule`, { date, time }).then((r) => r.data.label)}
                onBooked={(label) => setInfo({ ...info, rescheduled_to: label })} />
            )}
            {info.expired && <p className="text-sm text-slate-500" data-testid="appt-response-expired">Questo appuntamento è già passato.</p>}
            {canAct && (
              <div className="flex flex-wrap gap-3">
                {info.status !== "confermato" && (
                  <button className="btn-primary flex-1" disabled={busy} onClick={() => respond("conferma")} data-testid="appt-response-confirm-btn"><CheckCircle2 className="h-4 w-4" /> Confermo</button>
                )}
                <button className="btn-ghost flex-1 border border-rose-200 text-rose-700 hover:bg-rose-50" disabled={busy}
                  onClick={() => window.confirm("Vuoi davvero disdire l'appuntamento?") && respond("disdici")} data-testid="appt-response-cancel-btn">
                  <XCircle className="h-4 w-4" /> Disdico
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
